import {DialogBody} from '@cypro/ui/components'
import {Loader} from '@chakra-ui/react'
import {useTranslate} from '@tolgee/react'
import {dialog} from '@/modules/layout'
import {useEffect, useMemo} from 'react'
import {useGetPatientTherapySequence} from '@/modules/patients-therapies/api'
import {getAggregatedBatchInfo} from '@cypro/engine/utils'
import {useGetBatchesByIds} from '../../api/use-get-batches-by-ids'
import {useGetOrderById} from '../../api/use-get-order-by-id'
import {pdf} from '@react-pdf/renderer'
import {PickingListPrint} from '../picking-list-print'
import {useGetSettings} from '@/modules/settings/api/use-get-settings'
import {buildOrdersSolventRequirements} from '../picking-list/utils'
import {getDistributedOrderBatchIds} from '../../functions'

function onClose() {
  dialog.close('print-dialog')
}

export function openPrintDialog(sequenceId: string) {
  dialog.open('print-dialog', {
    children: <OpenPrintDialog sequenceId={sequenceId} />,
    contentProps: {minWidth: '50vw'},
  })
}

export interface OpenPrintDialogProps {
  sequenceId: string
}

export function OpenPrintDialog({sequenceId}: OpenPrintDialogProps) {
  const {t} = useTranslate('common')
  const [sequence] = useGetPatientTherapySequence(sequenceId)
  const [order, orderState] = useGetOrderById(sequence?.orderId)
  const uniqueBatchIds = useMemo(
    () => (order?.distributedOrders?.orders ?? []).flatMap(getDistributedOrderBatchIds),
    [order]
  )

  const [batches, batchesState] = useGetBatchesByIds(uniqueBatchIds)

  const aggregated = useMemo(() => {
    return getAggregatedBatchInfo(order?.distributedOrders?.orders || [], batches || [])
  }, [order, batches])

  const {settings, meta: settingsMeta} = useGetSettings()
  const solventRequirements = useMemo(
    () =>
      settings?.skipSolventStockCheck
        ? buildOrdersSolventRequirements(order?.distributedOrders?.orders || [], batches || [])
        : undefined,
    [settings?.skipSolventStockCheck, order, batches]
  )

  // Zero returns locally cached (possibly empty) results immediately, so wait until
  // the server has confirmed both queries before rendering the PDF.
  const isReady =
    !!order && orderState.type === 'complete' && batchesState.type === 'complete' && settingsMeta.type === 'complete'

  useEffect(() => {
    if (!isReady || !order) return
    let cancelled = false
    const handlePrint = async () => {
      const blob = await pdf(
        <PickingListPrint order={order} mapped={aggregated} t={t} solventRequirements={solventRequirements} />
      ).toBlob()
      if (cancelled) return
      const url = URL.createObjectURL(blob)
      window.open(url)
      onClose()
    }
    handlePrint()
    return () => {
      cancelled = true
    }
  }, [isReady, order, aggregated, t, solventRequirements])

  return (
    <DialogBody minHeight="none" overflow="auto">
      <Loader />
    </DialogBody>
  )
}
